import { ITaskSearchQuery } from 'app/models/Tasks/Tasks';
import { formatFilterValues } from '@AMIEWEB/Tasks/TaskManagement/TaskFilters/TaskFilterUtilities';

const mapIds = (arr: any) =>
  Array.isArray(arr)
    ? arr.map((id: any) => (typeof id === 'object' ? id.value : id))
    : [];

/** Resolve numeric facility id from a FACILITYSEARCH selection row (supports nested `{ value: { value: id } }`). */
const facilitySelectionToId = (choice: any): number => {
  if (choice == null) {
    return Number.NaN;
  }
  if (typeof choice !== 'object') {
    const n = Number(choice);
    return Number.isNaN(n) ? Number.NaN : n;
  }
  const nested = choice?.value?.value;
  if (nested != null && nested !== '') {
    const n = Number(nested);
    if (!Number.isNaN(n)) {
      return n;
    }
  }
  const direct = choice?.value;
  if (direct != null && typeof direct !== 'object') {
    const n = Number(direct);
    if (!Number.isNaN(n)) {
      return n;
    }
  }
  const fallback = choice?.fid ?? choice?.id;
  if (fallback != null && fallback !== '') {
    const n = Number(fallback);
    if (!Number.isNaN(n)) {
      return n;
    }
  }
  return Number.NaN;
};

/** Facility filter form value is either a simple id array or a FACILITYSEARCH aggregate (`selections`, `fid`, etc.). */
const getTaskFacilityIds = (facilityField: any): number[] => {
  if (facilityField == null || facilityField === '') {
    return [];
  }
  if (Array.isArray(facilityField)) {
    return facilityField
      .map((choice: any) => facilitySelectionToId(choice))
      .filter((id: number) => !Number.isNaN(id));
  }
  if (typeof facilityField === 'object') {
    const fromSelections = Array.isArray(facilityField.selections)
      ? facilityField.selections.map((choice: any) => facilitySelectionToId(choice)).filter((id: number) => !Number.isNaN(id))
      : [];
    const fromFid =
      facilityField.fid != null && facilityField.fid !== '' ? [Number(facilityField.fid)] : [];
    const ids = [...new Set([...fromSelections, ...fromFid])].filter(id => !Number.isNaN(id));
    return ids;
  }
  return [];
};

const hasNonEmptyArray = (v: unknown): v is any[] => Array.isArray(v) && v.length > 0;

/**
 * Use form scope for list fields when chip state never supplied values, supplied null/undefined,
 * or supplied an empty array while the form still has selections (chip layer out of sync with form).
 */
const shouldMergeArrayScopeField = (
  rawSelected: Record<string, unknown>,
  field: string,
  source: Record<string, any>,
): boolean => {
  if (!hasNonEmptyArray(source?.[field])) return false;
  if (rawSelected == null || !Object.prototype.hasOwnProperty.call(rawSelected, field)) return true;
  const rawVal = rawSelected[field];
  if (rawVal == null) return true;
  if (Array.isArray(rawVal) && rawVal.length === 0) return true;
  return false;
};

/** True when chip state never set this field (object / aggregate paths). */
const chipFieldAbsent = (rawSelected: Record<string, unknown>, field: string): boolean =>
  rawSelected == null || !Object.prototype.hasOwnProperty.call(rawSelected, field);

/** Copy scope fields from `source` onto `target` when `rawSelected` never set that chip key. */
const applyScopeFallbackFromSource = (
  rawSelected: Record<string, unknown>,
  source: Record<string, any>,
  target: Record<string, any>,
): Record<string, any> => {
  const merged = { ...target };

  if (shouldMergeArrayScopeField(rawSelected, 'recipientIds', source)) {
    merged.recipientIds = source.recipientIds;
  }
  if (shouldMergeArrayScopeField(rawSelected, 'createdByIds', source)) {
    merged.createdByIds = source.createdByIds;
  }
  if (shouldMergeArrayScopeField(rawSelected, 'orderIds', source)) {
    merged.orderIds = source.orderIds;
  }
  if (shouldMergeArrayScopeField(rawSelected, 'placementIds', source)) {
    merged.placementIds = source.placementIds;
  }
  if (chipFieldAbsent(rawSelected, 'facilityIds') && source?.facilityIds != null) {
    merged.facilityIds = source.facilityIds;
  }
  if (
    chipFieldAbsent(rawSelected, 'facility') &&
    chipFieldAbsent(rawSelected, 'facilityIds') &&
    source?.facility != null
  ) {
    merged.facility = source.facility;
  }
  if (shouldMergeArrayScopeField(rawSelected, 'candidateIds', source)) {
    merged.candidateIds = source.candidateIds;
  }
  if (chipFieldAbsent(rawSelected, 'candidateFilter') && source?.candidateFilter != null) {
    merged.candidateFilter = source.candidateFilter;
  }
  if (shouldMergeArrayScopeField(rawSelected, 'unitIds', source)) {
    merged.unitIds = source.unitIds;
  }
  if (chipFieldAbsent(rawSelected, 'unit') && source?.unit != null) {
    merged.unit = source.unit;
  }

  // Yes/No multi-selects: merge from form when chip Redux state never had them (or only []).
  (['optionApprovalFilter', 'followUpFilter', 'flaggedFilter', 'coverageFilter'] as const).forEach(field => {
    if (shouldMergeArrayScopeField(rawSelected, field, source) && hasNonEmptyArray(source[field])) {
      merged[field] = source[field];
    }
  });

  return merged;
};

const mergeFormScopeIntoChipModeChips = (
  chipFormatted: Record<string, any>,
  formFormatted: Record<string, any>,
  rawSelected: Record<string, unknown>,
): Record<string, any> => applyScopeFallbackFromSource(rawSelected, formFormatted, chipFormatted);

/**
 * formatFilterValues maps Yes/No arrays to booleans and *deletes* a boolean when both Yes and No
 * are selected (or nothing parseable) — meaning do not filter on that criterion. A plain
 * `typeof out[key] !== 'boolean'` merge cannot tell that from "chip path never set the boolean,"
 * so we only restore from the form when chip Redux never supplied a non-empty selection for that
 * filter (same idea as shouldMergeArrayScopeField).
 */
const mergeDerivedYesNoBooleansFromForm = (
  chips: Record<string, any>,
  formFormatted: Record<string, any>,
  rawSelected: Record<string, unknown>,
): Record<string, any> => {
  const out = { ...chips };
  (
    [
      ['isFollowUp', 'followUpFilter'],
      ['isOptionApproval', 'optionApprovalFilter'],
      ['isFlagged', 'flaggedFilter'],
      ['isCoverage', 'coverageFilter'],
    ] as const
  ).forEach(([boolKey, filterKey]) => {
    if (hasNonEmptyArray(rawSelected[filterKey])) {
      return;
    }
    if (typeof out[boolKey] !== 'boolean' && typeof formFormatted[boolKey] === 'boolean') {
      out[boolKey] = formFormatted[boolKey];
    }
  });
  return out;
};

export const transformToTaskSearchQuery = (
  values,
  selectedChipFilters,
  pageNumber,
  pageSize,
  sortedColumn,
): ITaskSearchQuery => {
  const filterRequest: ITaskSearchQuery = {
    pageNumber: pageNumber || 1,
    pageSize: pageSize || 150,
  };

  const formattedValues = formatFilterValues(values ?? {});

  const rawSelected =
    selectedChipFilters && typeof selectedChipFilters === 'object' && !Array.isArray(selectedChipFilters)
      ? (selectedChipFilters as Record<string, unknown>)
      : {};

  const formattedSelectedChipFilters = selectedChipFilters
    ? formatFilterValues(selectedChipFilters)
    : null;

  // Chip mode must run when only Yes/No sub-filter chips are toggled; those keys are not part of the
  // legacy taskStatusIds / createdByIds / recipientIds gate, and the form often still has [] until setValue syncs.
  const hasYesNoChipSelections =
    hasNonEmptyArray(rawSelected.optionApprovalFilter) ||
    hasNonEmptyArray(rawSelected.followUpFilter) ||
    hasNonEmptyArray(rawSelected.flaggedFilter) ||
    hasNonEmptyArray(rawSelected.coverageFilter);

  const hasChipFilters =
    formattedSelectedChipFilters &&
    (Array.isArray(formattedSelectedChipFilters.taskStatusIds) ||
      Array.isArray(formattedSelectedChipFilters.createdByIds) ||
      Array.isArray(formattedSelectedChipFilters.recipientIds) ||
      hasYesNoChipSelections);

  // Chip mode: chip selections drive sub-filters; scope fields absent from selectedChipFilters are
  // merged from the form only here (not in Redux) so the API and hasTasksRequiredFilters match the
  // form without marking those scope chips as chip-selected in the UI.
  let chips: Record<string, any> =
    hasChipFilters && formattedSelectedChipFilters
      ? mergeFormScopeIntoChipModeChips(formattedSelectedChipFilters, formattedValues, rawSelected)
      : formattedValues;

  // Re-run formatFilterValues on the merged chip+form snapshot so Yes/No arrays map to booleans
  // (chip-only payloads often omit or nest optionApprovalFilter differently than the dropdown path).
  if (hasChipFilters && chips) {
    chips = formatFilterValues(chips);
    chips = mergeDerivedYesNoBooleansFromForm(chips, formattedValues, rawSelected);
  }

  if (chips) {
    filterRequest.statuses = mapIds(chips.taskStatusIds);
    filterRequest.senderIds = mapIds(chips.createdByIds);
    filterRequest.recipientIds = mapIds(chips.recipientIds);
    filterRequest.categoryIds = mapIds(chips.taskCategories);
    let facilityIds = getTaskFacilityIds(chips.facilityIds);
    if (!facilityIds.length) {
      facilityIds = getTaskFacilityIds(chips.facility);
    }
    if (!facilityIds.length && hasChipFilters) {
      facilityIds = getTaskFacilityIds(formattedValues.facilityIds);
    }
    if (!facilityIds.length && hasChipFilters) {
      facilityIds = getTaskFacilityIds(formattedValues.facility);
    }
    if (facilityIds.length) {
      filterRequest.facilityIds = facilityIds;
    }
    filterRequest.orderIds = mapIds(chips.orderIds);
    filterRequest.placementIds = mapIds(chips.placementIds);

    if (chips.followUpDateMin) {
      filterRequest.followUpDateMin = chips.followUpDateMin;
    }
    if (chips.followUpDateMax) {
      filterRequest.followUpDateMax = chips.followUpDateMax;
    }
    if (typeof chips.isFollowUp === 'boolean') {
      filterRequest.isFollowUp = chips.isFollowUp;
    }
    if (typeof chips.isOptionApproval === 'boolean') {
      filterRequest.isOptionApproval = chips.isOptionApproval;
    }
    if (typeof chips.isFlagged === 'boolean') {
      filterRequest.isFlagged = chips.isFlagged;
    }
    if (typeof chips.isCoverage === 'boolean') {
      filterRequest.isCoverage = chips.isCoverage;
    }
    if (chips.sentDateMin) {
      filterRequest.sentDateMin = chips.sentDateMin;
    }
    if (chips.sentDateMax) {
      filterRequest.sentDateMax = chips.sentDateMax;
    }
    if (Array.isArray(chips.priorities)) {
      filterRequest.priorities = mapIds(chips.priorities);
    }

    if (Array.isArray(chips.candidateIds)) {
      const ids = mapIds(chips.candidateIds)
        .map((id: any) => Number(id))
        .filter((id: number) => !Number.isNaN(id));
      if (ids.length) {
        filterRequest.candidateIds = ids;
      }
    } else if (chips.candidateFilter) {
      const fromSelections = Array.isArray(chips.candidateFilter.selections)
        ? chips.candidateFilter.selections
            .map((choice: any) =>
              typeof choice === 'object' && choice !== null
                ? Number(choice?.value ?? choice?.id ?? choice?.candidateId)
                : Number(choice),
            )
            .filter((id: number) => !Number.isNaN(id))
        : [];
      const fromSubFilter =
        chips.candidateFilter.cid != null && chips.candidateFilter.cid !== ''
          ? [Number(chips.candidateFilter.cid)]
          : [];
      const ids = [...new Set([...fromSelections, ...fromSubFilter])].filter(
        (id: number) => !Number.isNaN(id),
      );
      if (ids.length) {
        filterRequest.candidateIds = ids;
      }
    }

    if (Array.isArray(chips.unitIds)) {
      const ids = mapIds(chips.unitIds)
        .map((id: any) => Number(id))
        .filter((id: number) => !Number.isNaN(id));
      if (ids.length) {
        filterRequest.unitIds = ids;
      }
    } else if (chips.unit) {
      const fromSelections = Array.isArray(chips.unit.selections)
        ? chips.unit.selections
            .map((choice: any) =>
              typeof choice === 'object' && choice !== null
                ? Number(choice?.value ?? choice?.unitId ?? choice?.id)
                : Number(choice),
            )
            .filter((id: number) => !Number.isNaN(id))
        : [];
      const fromSubFilter =
        chips.unit.unitId != null && chips.unit.unitId !== '' ? [Number(chips.unit.unitId)] : [];
      const ids = [...new Set([...fromSelections, ...fromSubFilter])].filter(
        (id: number) => !Number.isNaN(id),
      );
      if (ids.length) {
        filterRequest.unitIds = ids;
      }
    }
  }

  if (sortedColumn?.column && sortedColumn?.direction) {
    filterRequest.sortedColumn = {
      column: sortedColumn.column,
      direction: sortedColumn.direction,
    };
  }

  return filterRequest;
};

export const hasTasksRequiredFilters = (request: ITaskSearchQuery | null | undefined): boolean => {
  if (!request) return false;

  const hasFrom = Array.isArray(request.senderIds) && request.senderIds.length > 0;
  const hasRecipients = Array.isArray(request.recipientIds) && request.recipientIds.length > 0;
  const hasFacility = Array.isArray(request.facilityIds) && request.facilityIds.length > 0;
  const hasOid = Array.isArray(request.orderIds) && request.orderIds.length > 0;
  const hasPid = Array.isArray(request.placementIds) && request.placementIds.length > 0;
  const hasCandidate = Array.isArray(request.candidateIds) && request.candidateIds.length > 0;
  const hasUnit = Array.isArray(request.unitIds) && request.unitIds.length > 0;
  const hasCategory = Array.isArray(request.categoryIds) && request.categoryIds.length > 0;

  return (
    hasFrom ||
    hasRecipients ||
    hasFacility ||
    hasOid ||
    hasPid ||
    hasCandidate ||
    hasUnit ||
    hasCategory
  );
};
